// SEAT-DRAWER-UX §4 (docs/design/2026-10-28-seat-drawer-ux.md): the drawer's Finance tab below
// its month stat line. Budgets: one row per active budget on the seat, of every period type
// (limit, warning, cap or "alert only", used); a row opens the budget modal over `budgets.get`
// (period, limit, warnings, cap, actual, reserved, left, open incidents; Edit and Archive).
// Spend: the month's `seats.cost` roll-up by kind and, on a DE seat, the record's cost and
// supervisor time per outcome. Budgets are a company manager's read (`budgets.*`); the spend is
// any seat viewer's.
import { Button, LensBandDot, type LensBandTone } from "@companyos/ui";
import {
  queryOptions,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArchiveIcon, PencilIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import type { Api } from "../../api/client";
import type { BudgetDetail, BudgetRow } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import { budgetQuery, invalidateAgents } from "../agents/agent-queries";
import { BudgetEditDialog, budgetPeriodText } from "../agents/budgets-screen";
import { formatMicros } from "../agents/money";
import type { CompanyAccess } from "../company";
import { formatMinutes } from "../de/record-tab";
import {
  COST_KIND_LABELS,
  formatDateTime,
  formatMoney,
  formatPercent,
  fractionDigits,
} from "../format";
import { deRecordQuery, seatCostQuery } from "../queries";
import { Loading } from "../screen-parts";
import { RowAction } from "../settings/row-actions";
import { ErrorNotice } from "../structure/parts";
import { ConfirmRemove, ItemRow, MoreRowsLink } from "./seat-item-row";
import { RECORD_WEEKS } from "./seat-record";
import { topOf } from "./seat-rows";
import { PageArrow, SectionModal } from "./section-modal";

const PERIOD_ORDER: BudgetRow["period_type"][] = [
  "day",
  "week",
  "month",
  "quarter",
  "year",
  "standing",
];

/** The seat's active budgets, every period, shortest period first. */
export const seatBudgetsQuery = (api: Api, co: string, seatId: string) =>
  queryOptions({
    queryKey: ["company", co, "finance", "seat-budgets", seatId],
    queryFn: async () => {
      const out: BudgetRow[] = [];
      let cursor: string | undefined;
      for (let i = 0; i < 20; i++) {
        const p = await api.client.budgets.list({
          params: { co },
          query: {
            subject_kind: "seat",
            subject_id: seatId,
            state: "active",
            limit: 200,
            ...(cursor ? { cursor } : {}),
          },
        });
        out.push(...p.data);
        if (!p.next_cursor) break;
        cursor = p.next_cursor;
      }
      return out.sort(
        (a, b) => PERIOD_ORDER.indexOf(a.period_type) - PERIOD_ORDER.indexOf(b.period_type),
      );
    },
  });

const toMicros = (minor: number, currency: string) => minor * 10 ** (6 - fractionDigits(currency));

/** Used over the limit, as a fraction (actual + reserved), or null while it loads. */
function usedOf(d: BudgetDetail | undefined): number | null {
  if (!d) return null;
  const limit = toMicros(d.amount_minor, d.currency);
  return limit > 0 ? (d.actual_micros + d.reserved_micros) / limit : null;
}

/** Red over the limit, amber past the first warning, green under it. */
function bandOf(b: BudgetRow, used: number | null): LensBandTone {
  if (used === null) return "none";
  const warn = Number(b.soft_thresholds[0] ?? 0.8);
  return used >= 1 ? "red" : used >= warn ? "amber" : "green";
}
const BAND_WORD: Record<LensBandTone, string> = {
  green: "Within budget",
  amber: "Past the warning",
  red: "At or over the limit",
  none: "Loading",
};

/** "warn 80% · cap $1,500" or "warn 80% · alert only". */
function rulesText(b: BudgetRow): string {
  const warn = b.soft_thresholds.map((t) => formatPercent(Number(t))).join(", ");
  const cap =
    b.hard_cap_minor === null
      ? "alert only"
      : `cap ${formatMoney(b.hard_cap_minor, b.currency, { whole: true })}`;
  return `${warn ? `warn ${warn} · ` : ""}${cap}`;
}

const invalidateBudgets = (qc: ReturnType<typeof useQueryClient>, co: string) => {
  invalidateAgents(qc, co);
  void qc.invalidateQueries({ queryKey: ["company", co, "org-chart"] });
  void qc.invalidateQueries({ queryKey: ["company", co, "finance"] });
};

/**
 * The Budgets block: a manager's rows, each opening the budget modal; ✎ edits, the archive icon
 * archives (asked first). The All view shows the first rows and "+N more".
 */
export function SeatBudgets({
  api,
  company,
  seat,
  all,
  onMore,
  empty,
}: {
  api: Api;
  company: CompanyAccess;
  seat: { id: string; title: string };
  /** The All view: the first rows only. */
  all: boolean;
  onMore: () => void;
  /** The empty state (with the panel's Add budget). */
  empty: ReactNode;
}) {
  const q = useQuery(seatBudgetsQuery(api, company.id, seat.id));
  const rows = q.data ?? [];
  const { shown, more } = topOf(rows, all);
  const details = useQueries({
    queries: shown.map((b) => budgetQuery(api, company.id, b.id)),
  });
  const [open, setOpen] = useState<string | null>(null);
  const [editing, setEditing] = useState<BudgetRow | null>(null);
  const [archiving, setArchiving] = useState<BudgetRow | null>(null);
  const qc = useQueryClient();
  const archive = useMutation({
    mutationFn: (id: string) => api.client.budgets.archive({ params: { co: company.id, id } }),
    onSuccess: () => {
      setArchiving(null);
      setOpen(null);
    },
    onSettled: () => invalidateBudgets(qc, company.id),
  });
  if (q.isPending) return <Loading label="Loading the budgets" rows={1} />;
  if (q.isError) return <ErrorNotice what="The seat's budgets" error={q.error} />;
  return (
    <div className="flex flex-col gap-1" data-slot="seat-budgets">
      {rows.length === 0 ? (
        empty
      ) : (
        <ul className="flex flex-col gap-1">
          {shown.map((b, i) => {
            const d = details[i]?.data;
            const used = usedOf(d);
            const band = bandOf(b, used);
            const name = budgetPeriodText(b);
            return (
              <ItemRow
                key={b.id}
                item={b.id}
                slot="budget-row"
                title={name}
                name={name}
                onOpen={() => setOpen(b.id)}
                dot={<LensBandDot band={band} aria-label={BAND_WORD[band]} />}
                value={
                  <span className="text-xs">
                    {d ? formatMicros(d.actual_micros, d.currency) : "…"}
                    <span className="text-faint">
                      {" / "}
                      {formatMoney(b.amount_minor, b.currency, { whole: true })}
                    </span>
                  </span>
                }
                chip={
                  <span className="hidden shrink-0 text-[11px] text-faint sm:inline">
                    {rulesText(b)}
                  </span>
                }
                actions={
                  <>
                    <RowAction
                      icon={PencilIcon}
                      label={`Edit the ${name} budget`}
                      tip="Edit"
                      onClick={() => setEditing(b)}
                    />
                    <RowAction
                      icon={ArchiveIcon}
                      label={`Archive the ${name} budget`}
                      tip="Archive (its history stays)"
                      onClick={() => setArchiving(b)}
                    />
                  </>
                }
              />
            );
          })}
        </ul>
      )}
      <MoreRowsLink n={more} onClick={onMore} />
      {open ? (
        <BudgetDetailModal
          api={api}
          company={company}
          seat={seat}
          id={open}
          onEdit={(b) => setEditing(b)}
          onArchive={(b) => setArchiving(b)}
          onClose={() => setOpen(null)}
        />
      ) : null}
      {editing ? (
        <BudgetEditDialog
          api={api}
          company={company}
          budget={editing}
          description={`${seat.title} · ${budgetPeriodText(editing)}`}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {archiving ? (
        <ConfirmRemove
          title="Archive this budget?"
          description={`${budgetPeriodText(archiving)}: the seat stops holding it, and spend counts against the levels above. Its history is kept.`}
          confirmLabel="Archive"
          pending={archive.isPending}
          onConfirm={() => archive.mutate(archiving.id)}
          onClose={() => setArchiving(null)}
        />
      ) : null}
    </div>
  );
}

/** One budget, over `budgets.get`: its rules, its money, its open incidents; Edit and Archive. */
export function BudgetDetailModal({
  api,
  company,
  seat,
  id,
  onEdit,
  onArchive,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  seat: { title: string };
  id: string;
  onEdit: (b: BudgetRow) => void;
  onArchive: (b: BudgetRow) => void;
  onClose: () => void;
}) {
  const q = useQuery(budgetQuery(api, company.id, id));
  const b = q.data;
  const fact = (label: string, value: string, slot: string) => (
    <div>
      <dt className="text-faint">{label}</dt>
      <dd className="font-medium tabular-nums" data-field={slot}>
        {value}
      </dd>
    </div>
  );
  const left = b ? toMicros(b.amount_minor, b.currency) - b.actual_micros - b.reserved_micros : 0;
  return (
    <SectionModal
      title={b ? `${budgetPeriodText(b)} budget` : "Budget"}
      description={seat.title}
      info="Runs reserve against every level above them; crossing a warning opens an incident and alerts, and a hard cap refuses new runs until a raise is approved."
      onClose={onClose}
      slot="budget-detail"
      size="default"
      footer={
        <>
          {b && b.state === "active" ? (
            <Button
              type="button"
              variant="ghost"
              className="mr-auto text-danger-text"
              onClick={() => onArchive(b)}
              data-slot="budget-archive"
            >
              <ArchiveIcon data-icon="inline-start" aria-hidden="true" />
              Archive
            </Button>
          ) : null}
          {b && b.state === "active" ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => onEdit(b)}
              data-slot="budget-edit"
            >
              <PencilIcon data-icon="inline-start" aria-hidden="true" />
              Edit budget
            </Button>
          ) : null}
          <Button type="button" variant="secondary" onClick={onClose}>
            Done
          </Button>
        </>
      }
    >
      {q.isPending ? (
        <Loading label="Loading the budget" rows={2} />
      ) : q.isError ? (
        <ErrorNotice what="The budget" error={q.error} />
      ) : b ? (
        <>
          <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3" data-slot="budget-facts">
            {fact("Limit", formatMoney(b.amount_minor, b.currency), "limit")}
            {fact(
              "Warn at",
              b.soft_thresholds.map((t) => formatPercent(Number(t))).join(", ") || "—",
              "warn",
            )}
            {fact(
              "Hard cap",
              b.hard_cap_minor === null ? "Alert only" : formatMoney(b.hard_cap_minor, b.currency),
              "cap",
            )}
            {fact("Actual", formatMicros(b.actual_micros, b.currency), "actual")}
            {fact("Reserved by live runs", formatMicros(b.reserved_micros, b.currency), "reserved")}
            {fact(left < 0 ? "Over by" : "Left", formatMicros(Math.abs(left), b.currency), "left")}
          </dl>
          <section className="flex flex-col gap-1.5" data-slot="budget-incidents">
            <h3 className="text-xs font-medium text-faint">Open incidents</h3>
            {b.incidents.length ? (
              <ul className="flex flex-col gap-1 text-sm">
                {b.incidents.map((i) => (
                  <li key={i.id} className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-medium">
                      {i.kind === "threshold"
                        ? `Passed ${formatPercent(Number(i.threshold ?? 0))}`
                        : i.kind === "cap"
                          ? "Hit the hard cap"
                          : "A reservation was refused"}
                    </span>
                    <span className="tabular-nums text-muted-foreground">
                      {formatMoney(i.amount_minor, b.currency)} · {formatDateTime(i.created_at)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">None: spend is within the warnings.</p>
            )}
          </section>
        </>
      ) : null}
    </SectionModal>
  );
}

/**
 * The Spend block: the month's cost by kind (`seats.cost`), and on a DE seat the record's cost
 * and supervisor time per outcome (four weeks, as the Record tab reads it).
 */
export function SeatSpend({
  api,
  company,
  seatId,
  month,
  deId,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
  month: string;
  deId: string | null;
}) {
  const cost = useQuery(seatCostQuery(api, company.id, seatId, month));
  const record = useQuery({
    ...deRecordQuery(api, company.id, deId ?? "", RECORD_WEEKS),
    enabled: deId !== null,
  });
  const kinds = cost.data
    ? Object.entries(cost.data.rollup.by_kind)
        .filter(([, v]) => v !== 0)
        .sort(([, a], [, b]) => b - a)
    : [];
  const t = record.data?.has_data ? record.data.total : null;
  return (
    <div className="flex flex-col gap-1" data-slot="seat-spend">
      <h4 className="text-xs font-medium text-faint">Spend by kind</h4>
      {cost.isPending ? (
        <Loading label="Loading the spend" rows={1} />
      ) : cost.isError ? (
        <ErrorNotice what="The seat's spend" error={cost.error} />
      ) : kinds.length ? (
        <ul className="flex flex-col gap-0.5 text-xs" data-slot="spend-kinds">
          {kinds.map(([kind, amount]) => (
            <li key={kind} data-kind={kind} className="flex justify-between gap-2">
              <span className="text-secondary">{COST_KIND_LABELS[kind] ?? kind}</span>
              <span className="tabular-nums">{formatMoney(amount, cost.data.rollup.currency)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-faint">No spend this month.</p>
      )}
      {deId && t ? (
        <p className="text-xs text-muted-foreground tabular-nums" data-slot="spend-per-outcome">
          Cost / outcome{" "}
          {t.cost_per_outcome_minor === null
            ? "—"
            : formatMoney(t.cost_per_outcome_minor, record.data?.currency ?? "USD")}{" "}
          · Supervisor {formatMinutes(t.supervisor_minutes_per_outcome)} / outcome
        </p>
      ) : null}
    </div>
  );
}

/** The Finance footer's page links, each marked. */
export function FinanceLinks({
  company,
  manager,
  deId = null,
}: {
  company: CompanyAccess;
  manager: boolean;
  /** MONITORING-P2: the seat's DE, whose budget incidents a manager opens from here. */
  deId?: string | null;
}) {
  const cls = "text-xs text-primary-text hover:underline";
  return (
    <p className="flex flex-wrap gap-x-3 gap-y-1" data-slot="finance-links">
      {manager && deId ? (
        <Link
          to="/c/$slug/agents/budget-incidents"
          params={{ slug: company.slug }}
          search={(prev: Record<string, unknown>) => ({ ...keepFocus(prev), de: deId })}
          className={cls}
          data-page-link=""
          data-slot="open-budget-incidents"
        >
          Open Budget incidents
          <PageArrow />
        </Link>
      ) : null}
      {manager ? (
        <Link
          to="/c/$slug/setup/budgets"
          params={{ slug: company.slug }}
          search={keepFocus}
          className={cls}
          data-page-link=""
          data-slot="open-budgets"
        >
          Open Budgets
          <PageArrow />
        </Link>
      ) : null}
      <Link
        to="/c/$slug/kpis/reports/finance"
        params={{ slug: company.slug }}
        search={keepFocus}
        className={cls}
        data-page-link=""
        data-slot="open-bva"
      >
        Open Finance › Budget vs actual
        <PageArrow />
      </Link>
    </p>
  );
}
